import { Component, type ReactNode, type ErrorInfo } from 'react';

import { cn } from '@/app/components/ui/utils';
import { Button } from '@/app/components/ui/button';

interface ChunkErrorBoundaryProps {
  /** Child components to render */
  children: ReactNode;
  /** Optional custom fallback UI to display on error */
  fallback?: ReactNode;
  /** Additional CSS classes for the default fallback container */
  className?: string;
}

interface ChunkErrorBoundaryState {
  hasError: boolean;
  error: Error | null;
}

/**
 * Error boundary component for handling failed chunk loads from lazy-loaded routes.
 * Displays a user-friendly error message with retry functionality.
 * 
 * @example
 * ```tsx
 * <ChunkErrorBoundary>
 *   <Suspense fallback={<LoadingFallback />}>
 *     <Routes>...</Routes>
 *   </Suspense>
 * </ChunkErrorBoundary>
 * ```
 * 
 * **Validates: Requirements 3.4**
 */
export class ChunkErrorBoundary extends Component<
  ChunkErrorBoundaryProps,
  ChunkErrorBoundaryState
> {
  constructor(props: ChunkErrorBoundaryProps) {
    super(props);
    this.state = {
      hasError: false,
      error: null,
    };
  }

  static getDerivedStateFromError(error: Error): ChunkErrorBoundaryState {
    return {
      hasError: true,
      error,
    };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo): void {
    // Log error for debugging purposes
    console.error('ChunkErrorBoundary caught an error:', error);
    console.error('Component stack:', errorInfo.componentStack);
  }

  /**
   * Checks if the error is related to a failed chunk/module load.
   * Common patterns include dynamic import failures and chunk loading errors.
   */
  private isChunkLoadError(error: Error | null): boolean {
    if (!error) return false;
    
    const chunkErrorPatterns = [
      'Loading chunk',
      'Failed to fetch dynamically imported module',
      'Unable to preload CSS',
      'ChunkLoadError',
    ];
    
    return chunkErrorPatterns.some(
      (pattern) => error.message?.includes(pattern) || error.name?.includes(pattern)
    );
  }

  /**
   * Handles retry by reloading the page to fetch fresh chunks.
   */
  private handleRetry = (): void => {
    window.location.reload();
  };

  render(): ReactNode {
    const { hasError, error } = this.state;
    const { children, fallback, className } = this.props;

    if (hasError) {
      // Use custom fallback if provided
      if (fallback) {
        return fallback;
      }

      const isChunkError = this.isChunkLoadError(error);

      // Default fallback UI
      return (
        <div
          className={cn(
            'flex flex-col items-center justify-center min-h-screen bg-brand-sand px-4',
            className
          )}
          role="alert"
          aria-live="assertive"
        >
          <div className="flex flex-col items-center gap-6 text-center max-w-md">
            {/* Error icon */}
            <div 
              className="size-16 rounded-full bg-brand-sienna/10 flex items-center justify-center"
              aria-hidden="true"
            >
              <svg
                className="size-8 text-brand-sienna"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
                strokeWidth={2}
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
                />
              </svg>
            </div>

            {/* Error message */}
            <div className="space-y-2">
              <h2 className="text-xl font-semibold text-foreground">
                {isChunkError ? 'Failed to load page' : 'Something went wrong'}
              </h2>
              <p className="text-foreground/60">
                {isChunkError
                  ? 'There was a problem loading this page. This might be due to a network issue or an outdated version.'
                  : 'An unexpected error occurred. Please try again.'}
              </p>
            </div>

            {/* Retry button */}
            <Button
              onClick={this.handleRetry}
              className="bg-brand-sienna hover:bg-brand-sienna/90 text-white"
            >
              Refresh Page
            </Button>

            {/* Additional help text */}
            <p className="text-sm text-foreground/50">
              If the problem persists, please check your internet connection.
            </p>
          </div>
        </div>
      );
    }

    return children;
  }
}
